import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Link, Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import Project from "../../models/Project";
import ServiceStep from "./steps/ServiceStep";
import ReviewStep from "./steps/ReviewStep";
import TalentCandidate from "../../models/TalentCandidate";
import PositionStep from "./steps/PositionStep";

import {ProgressBar} from "react-step-progress-bar";
import LoadingBanner from "../../components/LoadingBanner";
import {useProjectForm} from "../../providers/ProjectFormProvider";
import NameStep from "./steps/NameStep";
import TimingStep from "./steps/TimingStep";
import LocationStep from "./steps/LocationStep";
import BudgetStep from "./steps/BudgetStep";
import DescriptionStep from "./steps/DescriptionStep";
import ServiceSearch from "./steps/ServiceSearch";
import PhaseDeliverablesStep from "./steps/PhaseDeliverablesStep";
import TeamSkillsStep from "./steps/TeamSkillsStep";
import TemplateCollapsableOverview from "./components/TemplateCollapsableOverview";
import TeamExperienceStep from "./steps/TeamExperienceStep";
import TeamIndustriesStep from "./steps/TeamIndustriesStep";
import ProjectVisibilityStep from "./steps/ProjectVisibilityStep";
import PageError from "../../components/errors/PageError";


const PositionStepLayout = React.forwardRef((props, ref) => {
    const {roleId} = useParams();

    return (
        <PositionStep ref={ref} roleId={roleId} {...props}/>
    )
})

const CreateProjectProgress = ({currentStep, ...props}) => {
    const {formModel, isProjectUpdating} = useProjectForm()


    let progress = Project.progressForStep(currentStep, formModel.isService());
    // Fix for showing progress text when percent is small, i.e 10% round up to 15
    // let progressPercent = progress > 0 ? Math.max(progress, 15) : progress;

    const navSections = useMemo(() => {
        if (!formModel || !currentStep) return [];

        let sections = [];
        let formSections = Project.formSections(formModel.isService());

        let prevSectionPercent = 0, totalPercent = 0;

        for (let [section, sectionPercent] of Object.entries(formSections)) {
            totalPercent += sectionPercent;

            sections.push((
                <p key={`${TB.slugify(section)}-nav`}
                   className={TB.mapClass({
                       "progress-section": true,
                       "active-section": progress >= prevSectionPercent && progress < totalPercent
                   })}
                   style={{flexBasis: sectionPercent + "%"}}>
                    {section}
                </p>
            ))
            prevSectionPercent = totalPercent;
        }
        return sections;
    }, [formModel, currentStep, isProjectUpdating]);


    if (!currentStep || currentStep.HideNavbar || formModel.isService()) {
        return null;
    }

    return (
        <div className="project-nav">

            <div className="project-nav-back">
                <Link to={'/client/projects/all'}>
                    <i className="fa fa-arrow-left mr-2"/>

                    {TB.tryTrans('projects.back_link')}

                </Link>

            </div>

            <div className="project-progress-container">
                <div className="project-progress-sections">
                    {navSections}
                </div>
                <div className="wizard-progress">
                    <ProgressBar
                        filledBackground={TB.config('color.light_green')}
                        height={30}
                        percent={progress}
                        unfilledBackground={'#C4C4C4'}/>
                    <p className="wizard-progress-title" style={{right: `${100 - progress}%`}}>
                        {progress}% <span className="ml-1">Complete</span>
                    </p>
                </div>
            </div>

            <div className="progress-spacer"/>

        </div>
    );

}

const CreateProject = (props) => {

    const {projectId, roleId} = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const {
        project,
        formModel,
        error,
        refetch,
        isLoading,
        isDisabled,
        onFormChange,
        isProjectUpdating,
        onFormReset,
        onFormSubmit,
    } = useProjectForm();

    const [searchParams, setSearchParams] = useSearchParams();

    const routeRef = useRef(null)
    const [stepDisabled, setStepDisabled] = useState(false);


    useEffect(() => {
        if (formModel?.id && parseInt(projectId) !== formModel?.id) {
            if (location.pathname.includes(formModel.lastStepRemaining()?.getPath(formModel))) {
                return;
            }
            return navigate({
                pathname: formModel.lastStepRemaining()?.getPath(formModel)
            }, {replace: true})
        }
    }, [projectId, formModel?.id, isLoading, project]);

    const trans = (string, replace = {}) => {
        let transKey = `create_project.${string}`;
        return TB.trans(transKey, replace);
    }

    const projectSteps = useMemo(() => {
        if (!formModel) return [];
         return project.currentSteps();
    }, [isDisabled, formModel, project?.positions?.length, projectId, isLoading]);

    const lastStepRemaining = useMemo(() => {
        if (!formModel) return null;
        return project.lastStepRemaining();
    }, [isDisabled, projectSteps?.length, location.pathname,projectId, project?.positions?.length]);

    const currentStepIndex = useMemo(() => {
        return TB.findIndex(projectSteps, (step, index) => {
            return location.pathname.includes(step.path)
        })
    }, [isDisabled, location.pathname, projectSteps?.length]);

    const currentStep = projectSteps[currentStepIndex];
    const nextStep = projectSteps[currentStepIndex + 1];
    const prevStep = projectSteps[currentStepIndex - 1];


    const isReviewing = searchParams.has('fromReview')
    const fromBidding = searchParams.has('fromBidding')
    const fromBrief = searchParams.has('fromBrief')
    const fromScope = searchParams.has('fromScope')

    const goToStep = async (step, replace = false) => {
        let nextRouteState = location.state || {};
        let search = '';

        if (isReviewing) {
            search = '?fromReview=true';
        } else if (project?.isService() && step.getStepName() === ServiceStep.getStepName()) {
            search = '?service=' + project.service.id
        }

        let route = {
            pathname: step.getPath(formModel || projectId),
            search,
        }


        if (replace) {
            return navigate(route, {replace: true, state: nextRouteState});
        }
        return navigate(route)
    }

    const shouldBeRedirected = useCallback(() => {
        return isReviewing || fromBidding || fromBrief || fromScope
    }, [searchParams?.toString()]);


    const handleRedirected = async (cancel = true) => {

        if (cancel && roleId === 'new' && !project?.isService()) {
            await setStepDisabled(true)

            let positions = project.filterValidPositions([...project.positions]);

            positions = TB.filter(positions, pos => TB.isDefined(pos?.id));

            // console.log('POSITIONS_LENGTH', positions.length);
            await onFormChange({positions, positions_count: positions.length});


            await setStepDisabled(false)
        }

        if (fromBrief) {
            return goToBrief();
        }
        if (fromScope) {
            return goToBrief('/scope');
        }
        if (fromBidding) {
            return goToBidding();
        }

        if (isReviewing) {
            return goToReview();
        }
    }


    const goToReview = () => {
        return navigate({
            pathname: ReviewStep.getPath(project || projectId),
            key: TB.uuid()
        }, {replace: true});
    }

    const goToBidding = () => {

        TalentCandidate.clearCache();
        Project.clearCache();

        let nextPath = project.detailLink()

        return navigate({
            key: TB.uuid(),
            pathname: nextPath
        });
    }

    const goToBrief = (tab = '') => {
        TalentCandidate.clearCache();
        Project.clearCache();

        let nextPath = project.briefStep()

        return navigate({
            key: TB.uuid(),
            pathname: nextPath + tab
        });
    }

    const onSubmit = async (params, exitApplication = false) => {

        // console.log('CreateProject @ onSubmit -> params', params);

        let currentStepComponent = routeRef.current;
        // console.log('CreateProject @ onSubmit -> params', currentStepComponent);
        try {
            let invalidField = await currentStepComponent?.invalidFields();

            console.log({invalidField})
            if (invalidField) {

                if (exitApplication) {
                    return saveAndExitInvalidForm();
                }
                return
            }
        } catch (e) {
            console.warn('ERROR: onSubmitValidateCheck', e);
        }

        let savedProject = null;

        if (params) {

            let validated = await currentStep?.hasCompleted(
                Project.make({
                    ...params
                }), project
            );

            // console.log('CreateProject @ onSubmit -> validated', validated);


            if (!validated) {
                await TB.event("CreateProject@Submit");
                if (exitApplication) return saveAndExitInvalidForm();
                else return;
            }

            if (currentStepComponent && currentStepComponent.shouldHandleSubmit(formModel)) {
                await currentStepComponent.onSubmit(formModel);

                if (exitApplication) {
                    return exitCreateProject();
                }

                return;
            } else if (projectId === 'new') {
                savedProject = Project.make({
                    ...formModel,
                    ...validated
                });
                await onFormChange(savedProject);

                if (savedProject && exitApplication) {
                    return exitCreateProject();
                }


                return goToStep(nextStep, true);
            } else {
                savedProject = await updateAndSaveProject({
                    ...validated
                });

                if (savedProject && exitApplication) {
                    return exitCreateProject();
                }
            }
        }

        console.log('CreateProject @ onSubmit -> this.currentStep()', {
            savedProject,
            currentStep: currentStep?.path,
            nextStep: nextStep?.path,
        });
        // console.log('onSubmit', shouldBeRedirected())
        // console.log('onSubmit', nextStep)
        if (!savedProject) {
            if (exitApplication) return saveAndExitInvalidForm();
            return;
        }

        if (shouldBeRedirected()) {
            return handleRedirected();
        }

        if (exitApplication) {
            return exitCreateProject();
        }

        if (nextStep) {
            return goToStep(nextStep);
        }

    }

    const updateAndSaveProject = async (projectProps) => {

        await setStepDisabled(true)

        let updatedProject = await onFormSubmit(projectProps).catch(console.warn);

        console.log('updateAndSaveProject', updatedProject)
        await setStepDisabled(false)
        /*
        * Update form with server errors
        * */
        if (!updatedProject) {

            if (Project.updateHadInputResponseError()) {
                await routeRef.current?.handleInputResponseError(Project.SyncRequest);
            } else if (Project.SyncRequest && Project.SyncRequest.didFail &&
                (Project.SyncRequest.errorType === TB.Request.ErrorTooLong ||
                    Project.SyncRequest.errorType === TB.Request.ErrorLocal) && Project.SyncRequest.response) {

                await TB.alert('error',
                    TB.trans('api.system_maintenance.title'),
                    TB.trans('api.system_maintenance.message', {code: 'XXX'}), [
                        {title: 'Okay'}
                    ]
                )
            } else if (Project.SyncRequest.response) {
                Project.SyncRequest.didHandleError = await Project.SyncRequest.handleError();
                if (Project.SyncRequest.didHandleError
                    && Project.SyncRequest.shouldRerun) {
                    return updateAndSaveProject(
                        projectProps
                    );
                }
            }
            return false;
        }

        return updatedProject;
    }

    const exitCreateProject = () => {
        return navigate('/client/projects/all', {replace: true});
    }

    const saveAndExitInvalidForm = async () => {
        let exitAnyways = await TB.alert('error', 'Are you sure?',
            'You have errors on this step. If you exit, any unsaved information will be lost. Do you still want to exit?',
            [
                {title: 'Yes, Exit'},
                {title: 'Cancel'}
            ]
        )

        if (exitAnyways) {
            return exitCreateProject();
        }
    }

    const onChange = (params) => {
        console.log('onChange', params)
        if (!TB.isEmpty(params)) {
            return onFormChange(params)
        }
    }

    const onGoBack = async (prevStep) => {
        if (isReviewing && TB.isDefined(formModel)) {
            await onFormReset()
        }

        if (shouldBeRedirected()) {
            return handleRedirected(true);
        }

        return goToStep(prevStep, true)
    }


    const shouldShowBackButton = (currentStep) => {

        if (isReviewing) return true;

        if (formModel.id === 'new') return true;

        // if (currentStep?.name === ProjectPreviewStep.name) return true;

        if (currentStep?.index <= 1) return false;

        let isLastStep = currentStep?.index === projectSteps.length - 1;

        return !isLastStep && formModel.id !== 'new';
    }

    const shouldShowExitButton = (currentStep) => {
        if (!formModel?.canEditScope()) {
            return false;
        }

        return formModel?.id !== 'new';
            // && currentStep?.name !== ProjectPreviewStep.name;
    }

    const getContinueButton = (currentStep) => {
        let isLastStep = currentStep?.index === projectSteps.length - 1;
        if (isLastStep && formModel.id !== 'new') {
            return "Finish"
        }

        // if (currentStep?.name === ProjectPreviewStep.name && isReviewing) {
        //     return "Get Started"
        // } else
        // if (currentStep?.slug === ScopeStep.slug) {
        //     return trans('continue_button')
        // }

        return trans(isReviewing ? 'update_button' : 'continue_button')
    }

    const toggleDisabled = (stepSubmitDisabled) => {
        return setStepDisabled(stepSubmitDisabled)
    }

    const onComplete = async () => {
        let setup_at = TB.moment().utc().toISOString()
        if (formModel.setup_at) {
            setup_at = formModel.setup_at;
        }
        await onChange({setup_at});

        console.log('onComplete')
        let updated = await updateAndSaveProject({setup_at});
        return navigate(updated?.detailLink(), {replace: true});
    }

    let renderFormActions = () => {

        return (
            <div className="wizard-footer">

                {currentStep && currentStep.StepTitle && <div className="col-auto p-0"/>}

                <div className={TB.mapClass("p-0 flex-1", {
                    'col-auto': TB.isDefined(currentStep?.StepTitle)
                })}>

                    <div className="wizard-nav-btns">
                        {shouldShowBackButton(currentStep) && (
                            <button
                                formNoValidate
                                type="button"
                                onClick={() => onGoBack(prevStep)}
                                disabled={isProjectUpdating || (stepDisabled && !(shouldBeRedirected()))}
                                className="btn btn-outline-dark">
                                {
                                    (fromBidding || fromBrief || fromScope) ? 'Cancel' :
                                        trans(isReviewing ? 'return_review_button' : 'back_button')
                                }
                            </button>
                        )}

                        {shouldShowExitButton(currentStep) && (
                            <button
                                formNoValidate
                                type="button"
                                onClick={() => onSubmit(formModel, true)}
                                disabled={isProjectUpdating || stepDisabled}
                                className="btn btn-outline-dark">
                                Save and Exit
                            </button>
                        )}

                        {currentStep?.showSubmitButton() && (
                            <button
                                formNoValidate
                                type="button"
                                onClick={() => onSubmit(formModel)}
                                disabled={isProjectUpdating || stepDisabled}
                                className="btn btn-primary">
                                {getContinueButton(currentStep)}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    if (formModel && projectId !== 'new' && !formModel.canEditScope()) {
        return <Navigate to={project.detailLink()} replace={true}/>
    }

    if (isLoading && !error) {
        return <LoadingBanner message="Loading Project" adjustOverlay/>;
    }

    if (error) {
        return <PageError onRetry={refetch}/>
    }

    if (!formModel) {
        return <div/>;
    }


    let stepProps = {
        ref: routeRef,
        project: formModel,
        toggleDisabled: toggleDisabled,
        isDisabled: isProjectUpdating || stepDisabled,
        onSubmit: onSubmit,
        onChange: onChange,
        goBack: () => onGoBack(prevStep),
        goToStep: goToStep,
        goToReview: goToReview,
        nextStep: nextStep,
        queryParams: {...Object.fromEntries(searchParams), projectId},
        navigate: navigate,
        location: location,
        handleRedirected: handleRedirected,
        shouldBeRedirected: shouldBeRedirected,
        completeWizard: onComplete,
        goToNextStep: () => {

            if (shouldBeRedirected()) {
                return handleRedirected();
            }

            if (nextStep) {
                return goToStep(nextStep);
            }
        }
    }

    let defaultRoute = lastStepRemaining && lastStepRemaining?.getPath(formModel || projectId);

    return (
        <div className="wizard-container">

            <CreateProjectProgress currentStep={currentStep}/>

            <div className="wizard-step-container">

                <div className={TB.mapClass({
                    "wizard-team-container": currentStep && currentStep !== ReviewStep && !currentStep.HideNavbar && formModel?.isService?.()
                })}>
                    {currentStep && currentStep !== ReviewStep && !currentStep.HideNavbar && formModel?.isService?.() && (
                        <TemplateCollapsableOverview currentStep={currentStep}/>
                    )}
                <Routes>
                    <Route index element={<Navigate to={defaultRoute} replace={true}/>}/>

                    <Route path={'select-service/*'}>
                        <Route path={'teams/:cId?/find-project-type'} element={<ServiceSearch {...stepProps} ref={undefined}/>}/>
                        <Route path="*" element={<ServiceStep {...stepProps}>{renderFormActions()}</ServiceStep>}/>
                    </Route>
                    <Route path={'name'} element={<NameStep {...stepProps}>{renderFormActions()}</NameStep>}/>
                    <Route path={'kickoff'} element={<TimingStep {...stepProps}>{renderFormActions()}</TimingStep>}/>
                    <Route path={'location'} element={<LocationStep {...stepProps}>{renderFormActions()}</LocationStep>}/>
                    <Route path={'budget'} element={<BudgetStep {...stepProps}>{renderFormActions()}</BudgetStep>}/>

                    <Route path={'description'} element={<DescriptionStep {...stepProps}>{renderFormActions()}</DescriptionStep>}/>
                    <Route path={'deliverables'} element={<PhaseDeliverablesStep {...stepProps}>{renderFormActions()}</PhaseDeliverablesStep>}/>
                    <Route path={'skills'} element={<TeamSkillsStep {...stepProps}>{renderFormActions()}</TeamSkillsStep>}/>
                    <Route path={'industries'} element={<TeamIndustriesStep {...stepProps}>{renderFormActions()}</TeamIndustriesStep>}/>
                    <Route path={'experience'} element={<TeamExperienceStep {...stepProps}>{renderFormActions()}</TeamExperienceStep>}/>

                    <Route path={'role/new/*'} element={<PositionStep {...stepProps} roleId={'new'}>{renderFormActions()}</PositionStep>}/>

                    <Route path={'role/:roleId/*'} element={<PositionStepLayout {...stepProps}>{renderFormActions()}</PositionStepLayout>}/>

                    {/*<Route path={'post-on-wripple-job-board'} element={<PostsOnJobBoardStep {...stepProps}>{renderFormActions()}</PostsOnJobBoardStep>}/>*/}
                    <Route path={'project-visibility-settings'} element={<ProjectVisibilityStep {...stepProps}>{renderFormActions()}</ProjectVisibilityStep>}/>

                    <Route path={'review'} element={<ReviewStep {...stepProps}>{renderFormActions()}</ReviewStep>}/>

                    <Route path="*" element={<Navigate to={defaultRoute} replace={true}/>}/>
                </Routes>
                </div>
            </div>
        </div>
    )
}
export default CreateProject;